import {
  convertFromRaw,
  convertToRaw,
  Modifier,
  Entity,
  SelectionState,
  ContentBlock,
  CharacterMetadata,
  EditorState,
} from 'draft-js';

import { convertFromRaw as convertFromRawOurs } from './draft';

import { fromJS, Map, List } from 'immutable';

/**
 * Add img block and text block to rteState
 *
 * @param rteState
 * @param linkText
 * @param linkUrl
 */
export const addLinkToRTEState = (rteState, linkText, linkUrl) => {
  var contentState = convertFromRaw(rteState.toJS());
  var contentBlock = _createContentBlock(linkText, 'unstyled');

  /**
   * Add new block to content state
   */
  contentState = contentState.setIn(
    ['blockMap', contentBlock.getKey()],
    contentBlock,
  );

  var entityKey = _createEntityMapBlock('LINK', 'MUTABLE', {
    title: linkText,
    url: linkUrl,
  });
  var selectionState = _createSelectionStateForBlock(
    contentState,
    contentState.getLastBlock().getKey(),
  );

  contentState = Modifier.applyEntity(contentState, selectionState, entityKey);

  return fromJS(convertToRaw(contentState));
};

/**
 * Add img block and text block to rteState
 *
 * @param rteState
 * @param imgTitle
 * @param imgSrc
 */
export const addImageToRTEState = (rteState, imgTitle, imgSrc, s3Key) => {
  var contentState = convertFromRaw(rteState.toJS());
  var imgBlock = _createContentBlock(' ', 'atomic');
  var textBlock = _createContentBlock(imgTitle);

  /**
   * Add new block to content state
   */
  contentState = contentState.update('blockMap', (blockMap) => {
    return blockMap
      .set(imgBlock.getKey(), imgBlock)
      .set(textBlock.getKey(), textBlock);
  });

  const entityData = {
    src: imgSrc,
    height: 'auto',
    width: '100%',
  };

  if (s3Key) entityData.s3Key = s3Key;

  var entityKey = _createEntityMapBlock('IMAGE', 'MUTABLE', entityData);
  var selectionState = _createSelectionStateForBlock(
    contentState,
    imgBlock.getKey(),
  );

  contentState = Modifier.applyEntity(contentState, selectionState, entityKey);

  return fromJS(convertToRaw(contentState));
};

/**
 * Add text block to rteState
 *
 * @param rteState
 * @param text
 */
export const addTextToRTEState = (rteState, text) => {
  var contentBlock = _createContentBlock(text);
  var contentState = convertFromRaw(rteState.toJS());

  /**
   * Add new block to content state
   */
  contentState = contentState.setIn(
    ['blockMap', contentBlock.getKey()],
    contentBlock,
  );

  return fromJS(convertToRaw(contentState));
};

/**
 * Append text to rteState
 *
 * How is this different than addTextToRTEState? This just
 * adds text to an existing block. addTextToRTEState adds a
 * whole new text block.
 *
 * @param rteState
 * @param text
 */
export const appendTextToRTEState = (rteState, text) => {
  if (!rteState || !rteState.get('blocks') || !rteState.getIn(['blocks', 0]))
    return rteState;

  const blockIndex = rteState.get('blocks').size - 1;

  return rteState.updateIn(['blocks', blockIndex], (block) => {
    return block.set('text', `${block.get('text')}${text}`);
  });
};

/**
 * Get raw rteState from EditorState
 *
 * @param editorState
 * @returns rteState
 */
export const getRawRTEStateFromEditorState = (editorState) => {
  const contentState = editorState.getCurrentContent();
  return convertToRaw(contentState);
};

/**
 * Convert raw rteState to editorState
 *
 * @param rteState
 * @param {Boolean} moveSelectionToEnd
 * @returns EditorState;
 */
export const createEditorStateFromRawRTEState = (
  rteState,
  moveSelectionToEnd,
) => {
  let editorState = EditorState.createWithContent(convertFromRawOurs(rteState));
  return moveSelectionToEnd
    ? EditorState.moveSelectionToEnd(editorState)
    : editorState;
};

/**
 * Clears all RTE styles
 *
 * @param rteState
 * @returns rteState;
 */
export const clearRTEStyles = (rteState) => {
  if (!rteState) return rteState;
  const blocks = rteState.get('blocks');

  if (!blocks) return rteState;

  const newBlocks = blocks.map((block) => {
    const newBlock = block.set('inlineStyleRanges', List());
    return newBlock.deleteIn(['data', 'text-align']);
  });

  const newState = rteState.set('blocks', newBlocks);

  return newState;
};

/**
 * Clear a specific type of style
 *
 * @param rteState
 * @returns rteState;
 */
export const clearRTEStyle = (rteState, query) => {
  if (!rteState) return rteState;
  let blocks = rteState.get('blocks');

  if (!blocks) return rteState;

  let newBlocks = blocks.map((block) => {
    let inlineStyles = block.get('inlineStyleRanges');
    let newInlineStyles = inlineStyles.filter(
      (style) => style.get('style').includes(query) === false,
    );

    let newBlock = block.set('inlineStyleRanges', newInlineStyles);

    return newBlock;
  });

  let newState = rteState.set('blocks', newBlocks);

  return newState;
};

/**
 * Create New Content Block for the rteState blocks list
 *
 * @param text
 * @param entityRange
 */
function _createContentBlock(text, type) {
  let characterList = [];

  for (var i = 0; i < text.length; i++) {
    characterList.push(new CharacterMetadata());
  }

  return new ContentBlock({
    key: _generateRandomKey(),
    text: text || '',
    depth: 0,
    data: Map(),
    type: type || 'unstyled',
    characterList: List(characterList),
  });
}

/**
 * Generate a random key from content block
 *
 * https://github.com/facebook/draft-js/blob/master/src/model/keys/generateRandomKey.js
 */
const seenKeys = {};
const MULTIPLIER = Math.pow(2, 24);

function _generateRandomKey() {
  let key;

  while (key === undefined || seenKeys.hasOwnProperty(key) || !isNaN(+key)) {
    key = Math.floor(Math.random() * MULTIPLIER).toString(32);
  }

  seenKeys[key] = true;

  return key;
}

export const generateRandomKey = _generateRandomKey;

/**
 * Create Entity Range for applying entity to block
 *
 * @param key
 * @param length
 * @param offset
 */
// eslint-disable-next-line no-unused-vars
function _createEntityRange(key, length, offset) {
  return fromJS({ key, length, offset });
}

/**
 * Create Entity Map
 *
 * @param type - 'LINK', 'PHOTO', etc.
 * @param mutability - 'MUTABLE', 'IMMUTABLE', etc.
 * @param data
 *
 * @returns Entity
 */
function _createEntityMapBlock(type, mutability, data) {
  return Entity.create(type, mutability, data);
}

/**
 * Create new selection state
 *
 * Selection points are tracked as key/offset pairs, where the key value is
 * the key of the ContentBlock where the point is positioned and the offset
 * value is the character offset within the block.
 *
 * @param anchorKey
 * @param anchorOffset
 * @param focusKey
 * @param focusOffset
 *
 * @returns SelectionState
 */
function _createSelectionState(anchorKey, anchorOffset, focusKey, focusOffset) {
  return new SelectionState({ anchorKey, anchorOffset, focusKey, focusOffset });
}

/**
 * Get selection state for block for a specified block key
 *
 * @param contentState
 * @param blockKey
 *
 * @returns SelectionState;
 */
function _createSelectionStateForBlock(contentState, blockKey) {
  let block = contentState.getBlockForKey(blockKey);

  return _createSelectionState(
    block.getKey(),
    0,
    block.getKey(),
    block.getLength(),
  );
}

/**
 * Get default immtuable rte state object
 */
function _getDefaultRTEState() {
  let currentContent = EditorState.createEmpty().getCurrentContent();
  return fromJS(convertToRaw(currentContent));
}

export const getDefaultRTEState = _getDefaultRTEState;

/**
 * Get placeholder state for corrupted cells
 */
export function getCorruptRTEStatePlaceholder() {
  return {
    blocks: [
      {
        data: {},
        entityRanges: [
          {
            key: 0,
            length: 1,
            offset: 0,
          },
        ],
        inlineStyleRanges: [],
        depth: 0,
        type: 'atomic',
        text: ' ',
        key: '9rinj',
      },
      {
        data: {},
        entityRanges: [],
        inlineStyleRanges: [],
        depth: 0,
        type: 'unstyled',
        text: '',
        key: '5kln8',
      },
    ],
    entityMap: {
      0: {
        data: {
          width: '100%',
          height: 'auto',
          src: `${window.shofloAssetBaseUrl}/img/rtecellmessage.png`,
        },
        mutability: 'MUTABLE',
        type: 'IMAGE',
      },
    },
  };
}

export const applyInlineFormattingToEditorState = (editorState, styles) => {
  let contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();

  editorState.getCurrentInlineStyle().forEach((style) => {
    if (style.startsWith('color') || style.startsWith('bgcolor')) {
      contentState = Modifier.removeInlineStyle(
        contentState,
        selectionState,
        style,
      );
    }
  });

  styles.forEach((style) => {
    contentState = Modifier.applyInlineStyle(
      contentState,
      selectionState,
      style,
    );
  });

  editorState = EditorState.push(
    editorState,
    contentState,
    'change-inline-style',
  );

  return editorState;
};
